import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/catalyst-ui/button'
import { Heading } from '@/components/catalyst-ui/heading'
import { Input } from '@/components/catalyst-ui/input'
import { Field, Label } from '@/components/catalyst-ui/fieldset'
import { Radio, RadioGroup, RadioField } from '@/components/catalyst-ui/radio'
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from '@/components/catalyst-ui/dialog'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/solid'
import DomainInfoCard from '@/features/domains/components/DomainInfoCard'
import DNSRecordsTable from '@/features/domains/components/DNSRecordsTable'
import AddDNSRecordModal from '@/features/domains/components/AddDNSRecordModal'
import { Badge } from '@/components/catalyst-ui/badge'
import { PageLoader } from '@/components/ui/PinwheelLoader'
import chevronLeft from "@/assets/icons/16/chevron-left.svg"
import { EditIcon16 } from '@/assets/icons'

import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/components/catalyst-ui/dropdown'
import { useActiveWorkspace } from '@/state/selection.store'
import { useProjects } from '@/features/workspace/hooks/useProjects'
import { useWorkspaceDomainsWithNs, useListUserDomains } from '../hooks/useMyDomains'
import { useDomainInfoJson, useUpdateDomainContacts, useModifyDomainSettings, useUpdateDomainAutoRenew, useUpdateNameservers, useWhoisPrivacyState, useUpdateWhoisPrivacy, useAddDomainWithNs, useCheckDomainWithNsStatus } from '../hooks/useMyDomains'
import { useDNSRecords, useAddDNSRecords, useUpdateDNSRecord, useDeleteDNSRecord } from '../hooks/useDNSRecords'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { useQueryClient } from '@tanstack/react-query'
import ConnectProjectDropdown from '@/features/domains/components/ConnectProjectDropdown'
import { usePermissions } from '@/hooks/usePermissions'
import EditDNSRecordDialog from '@/features/domains/components/EditDNSRecordDialog'
import DeleteDNSRecordDialog from '@/features/domains/components/DeleteDNSRecordDialog'
import EditRegistrantContactDialog from '@/features/domains/components/EditRegistrantContactDialog'
import EditNameserversDialog from '@/features/domains/components/EditNameserversDialog'
import AutoRenewalSettingsDialog from '@/features/domains/components/AutoRenewalSettingsDialog'
import WhoisPrivacySettingsDialog from '@/features/domains/components/WhoisPrivacySettingsDialog'
// import { buildWorkspaceUrl } from '@/lib/utils' // Not needed with simple routing

export default function DomainDetailsPage() {
  const { domainName } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { workspaceId } = useActiveWorkspace()
  const workspaceIdFromUrl = searchParams.get('workspace')
  const effectiveWorkspaceId = workspaceId || workspaceIdFromUrl
  const { projects } = useProjects(effectiveWorkspaceId)
  const { data: wsDomains } = useWorkspaceDomainsWithNs(effectiveWorkspaceId)
  const { user } = useAuth()
  const { data: userDomainsData } = useListUserDomains(user?.id, { workspaceId: effectiveWorkspaceId, offset: 0, limit: 100 })
  const addDomainWithNsMutation = useAddDomainWithNs()
  const checkStatusMutation = useCheckDomainWithNsStatus(false)
  const { hasRole } = usePermissions()
  const isViewer = hasRole('viewer')
  
  if (!domainName) {
    return (
      <div className="max-w-[1200px] mx-auto space-y-6 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-4">⚠️ No domain specified</div>
            <p className="text-gray-600 mb-4">Please select a domain to view details.</p>
          </div>
        </div>
      </div>
    )
  }
  
  // Fetch real domain info from API (OpenSRS data - doesn't have our DB auto_renew)
  const { 
    data: domain, 
    isLoading: isLoadingDomain, 
    isError: isDomainError,
    error: domainError 
  } = useDomainInfoJson(domainName)
  
  // Also fetch workspace domain to get our DB fields (auto_renew, project_id, etc.)
  // This gives us the correct auto_renew status from our database
  const wsMatch = (wsDomains?.data || []).find((d) => {
    const n = d.domain_name || d.name || d.domain
    return String(n).toLowerCase() === String(domainName).toLowerCase()
  })
  
  // Fetch domain price for renewal price estimate (1-year price)
  const [renewalPrice, setRenewalPrice] = useState(null)
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const { getDomainPrice } = await import('../api')
        const priceData = await getDomainPrice(domainName)
        // The API returns price for 1 year, which is the renewal price
        setRenewalPrice(priceData?.price || priceData?.data?.price)
      } catch (error) {
        console.error('Failed to fetch domain price:', error)
      }
    }
    if (domainName) {
      fetchPrice()
    }
  }, [domainName])
  
  // Merge: prefer workspace data for auto_renew, but use OpenSRS data for everything else
  const mergedDomain = domain ? {
    ...domain,
    renewal: {
      ...domain?.renewal,
      price: renewalPrice || domain?.renewal?.price || 'Not available'
    },
    data: {
      ...domain?.data,
      auto_renew: wsMatch?.auto_renew ?? domain?.data?.auto_renew,
      autoRenew: wsMatch?.auto_renew ?? domain?.data?.autoRenew,
      project_id: wsMatch?.project_id ?? domain?.data?.project_id,
    }
  } : null

  
  // Fetch real DNS records from API
  const { 
    data: dnsData, 
    isLoading: isLoadingDNS, 
    isError: isDNSError,
    error: dnsError 
  } = useDNSRecords(domainName)

  // Mutation for adding DNS records
  const addRecordsMutation = useAddDNSRecords()
  const updateRecordMutation = useUpdateDNSRecord()
  const deleteRecordMutation = useDeleteDNSRecord()
  
  // Mutation for updating domain contacts
  const updateContactsMutation = useUpdateDomainContacts()
  
  // Mutation for modifying domain settings
  const modifySettingsMutation = useModifyDomainSettings()
  const updateAutoRenewMutation = useUpdateDomainAutoRenew()
  
  // Mutation for updating nameservers
  const updateNameserversMutation = useUpdateNameservers()
  
  // Mutation for updating WHOIS privacy
  const updateWhoisPrivacyMutation = useUpdateWhoisPrivacy()
  // Also fetch WHOIS privacy state for this domain so modal defaults are correct
  const { data: whoisQueryResp, isFetching: isWhoisFetching } = useWhoisPrivacyState(domainName, { enabled: !!domainName });
  const queryClient = useQueryClient();

  // Determine connection status (withNS domains)
  // Try workspace list (most reliable for withNS)
  const connectedProjectId = (
    wsMatch?.project_id ||
    mergedDomain?.project_id ||
    mergedDomain?.projectId ||
    (typeof mergedDomain?.project === 'string' ? mergedDomain?.project : (mergedDomain?.project?._id || mergedDomain?.project?.id)) ||
    mergedDomain?.data?.project_id ||
    mergedDomain?.data?.projectId ||
    (typeof mergedDomain?.data?.project === 'string' ? mergedDomain?.data?.project : (mergedDomain?.data?.project?._id || mergedDomain?.data?.project?.id))
  )
  const isProjectConnected = Boolean(connectedProjectId)
  const projectName = projects?.find((p)=> (p._id||p.id) === String(connectedProjectId))?.name

  // Check if a project is already connected to ANY domain (including current domain)
  const isProjectConnectedToAnyDomain = (projectId) => {
    const pid = String(projectId)
    
    // Check withNS domains
    const list = wsDomains?.data || []
    const withNsHit = list.some((d) => {
      const connectedPid = String(
        d.project_id ||
        d.projectId ||
        (typeof d.project === 'string' ? d.project : (d.project?._id || d.project?.id) || '')
      )
      return connectedPid && connectedPid === pid
    })

    // Also consider third-party domains connected to projects
    const thirdPartyList = userDomainsData?.data || []
    const thirdPartyHit = thirdPartyList.some((item) => {
      const connectedPid = String(
        typeof item.project === 'string' ? item.project : (item.project?._id || item.project?.id) || ''
      )
      return connectedPid && connectedPid === pid
    })

    return withNsHit || thirdPartyHit
  }

  // Transform API data to match existing component structure
  const dnsRecords = dnsData?.data?.records?.map((record, index) => {
    // Log the raw record to debug TTL issues
    if (index === 0) {
      console.log('🔍 DNS Record from API:', record);
      console.log('🔍 TTL value:', record.ttl, typeof record.ttl);
    }
    
    return {
      id: `${record.type}_${index}`,
      type: record.type,
      host: record.subdomain || '@',
      value: record.address,
      ttl: record.ttl || record.TTL || 3600, // Try multiple possible field names
      priority: record.priority || null,
      nameservers_ok: true, // Assuming true for now, could be determined from API
      // Store original data for API calls
      originalRecord: record
    };
  }) || []
  
  // State management
  const [showAddRecordModal, setShowAddRecordModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const recordsPerPage = 6

  // DNS Record edit/delete states
  const [showEditRecordModal, setShowEditRecordModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [deletingRecord, setDeletingRecord] = useState(null)
  const [tableKey, setTableKey] = useState(0) // Force re-render to close dropdowns

  // Edit modal states
  const [showAddressEditModal, setShowAddressEditModal] = useState(false)
  const [showNameserversEditModal, setShowNameserversEditModal] = useState(false)
  const [showAutoRenewalModal, setShowAutoRenewalModal] = useState(false)
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)
  const [editingAddress, setEditingAddress] = useState(null)
  const [editingNameservers, setEditingNameservers] = useState([])
  const [autoRenewalEnabled, setAutoRenewalEnabled] = useState(domain?.autoRenewal || false)
  const [privacySetting, setPrivacySetting] = useState('public')

  const workspace = searchParams.get('workspace')
  const project = searchParams.get('project')

  // Update autoRenewal state when domain data loads
  useEffect(() => {
    const autoRenew = mergedDomain?.data?.auto_renew ?? mergedDomain?.data?.autoRenew ?? mergedDomain?.autoRenew
    if (autoRenew !== undefined) {
      setAutoRenewalEnabled(autoRenew)
    }
  }, [mergedDomain?.data?.auto_renew, mergedDomain?.data?.autoRenew, mergedDomain?.autoRenew])

  // Sync the privacySetting with API query response
  useEffect(() => {
    const currentPrivacyState = whoisQueryResp?.data?.whoisPrivacyState || domain?.data?.whoisPrivacyState;
    const isPrivate = currentPrivacyState === 'enable' || currentPrivacyState === true || String(currentPrivacyState).toLowerCase() === 'true';
    setPrivacySetting(isPrivate ? 'private' : 'public');
  }, [whoisQueryResp, domain?.data?.whoisPrivacyState, domainName]);

  const handleBackToMyDomains = () => {
    const ws = searchParams.get('workspace')
    navigate(`/domains/my-domains${ws ? `?workspace=${ws}` : ''}`)
  }

  const handleEdit = () => {
    // Navigate to domain editing page or show modal
  }

  const handleAddRecord = () => {
    setShowAddRecordModal(true)
  }

  const handleDeleteRecord = (record) => {
    setDeletingRecord(record)
    setShowDeleteConfirmModal(true)
  }

  const handleEditRecord = (record) => {
    setEditingRecord({
      ...record,
      // Create editable version
      editType: record.type,
      editHost: record.host,
      editValue: record.value,
      // editTtl: record.ttl, // TTL editing removed
      editPriority: record.priority || ''
    })
    setShowEditRecordModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingRecord) return
    
    try {
      // Create match object for API
      const match = {
        type: deletingRecord.type,
        subdomain: deletingRecord.host === '@' ? '' : deletingRecord.host,
        address: deletingRecord.value
      }

      // Add priority for MX records
      if (deletingRecord.type === 'MX' && deletingRecord.priority) {
        match.priority = deletingRecord.priority
      }

      await deleteRecordMutation.mutateAsync({
        domain: domainName,
        match
      })
      
      setShowDeleteConfirmModal(false)
      setDeletingRecord(null)
      setTableKey(prev => prev + 1) // Force table re-render to close dropdowns
    } catch (error) {
      console.error('Failed to delete DNS record:', error)
      // You might want to show an error toast here
    }
  }

  const handleSaveEditRecord = async () => {
    if (!editingRecord) return
    
    try {
      // Create match object (original record)
      const match = {
        type: editingRecord.type,
        subdomain: editingRecord.host === '@' ? '' : editingRecord.host,
        address: editingRecord.value
      }

      // Add priority for original MX record
      if (editingRecord.type === 'MX' && editingRecord.priority) {
        match.priority = editingRecord.priority
      }

      // Create update object (new values)
      // TTL editing removed - not updating TTL
      const update = {
        address: editingRecord.editValue
      }
      
      // Ensure type and subdomain are included in update if they exist
      if (editingRecord.type) {
        update.type = editingRecord.type;
      }
      if (editingRecord.host !== undefined) {
        update.subdomain = editingRecord.host === '@' ? '' : editingRecord.host;
      }

      // Add priority for new MX record
      if (editingRecord.editType === 'MX' && editingRecord.editPriority) {
        update.priority = parseInt(editingRecord.editPriority, 10)
      }

      console.log('Updating DNS record:', { match, update, ttlValue: finalTtl });
      
      await updateRecordMutation.mutateAsync({
        domain: domainName,
        match,
        update
      })
      
      setShowEditRecordModal(false)
      setEditingRecord(null)
      setTableKey(prev => prev + 1) // Force table re-render to close dropdowns
    } catch (error) {
      console.error('Failed to update DNS record:', error)
      // You might want to show an error toast here
    }
  }

  const handleSaveNewRecord = async (newRecord) => {
    try {
      // Transform the newRecord to match API format
      const recordToAdd = {
        type: newRecord.type,
        subdomain: newRecord.host === '@' ? '' : newRecord.host,
        address: newRecord.value,
        ttl: parseInt(newRecord.ttl )
      }

      // Add priority for MX records
      if (newRecord.type === 'MX' && newRecord.priority) {
        recordToAdd.priority = parseInt(newRecord.priority)
      }

      await addRecordsMutation.mutateAsync({
        domain: domainName,
        records: [recordToAdd]
      })
      
      setShowAddRecordModal(false)
      setTableKey(prev => prev + 1) // Force table re-render to close dropdowns
    } catch (error) {
      console.error('Failed to add DNS record:', error)
      // You might want to show an error toast or modal here
    }
  }

  // Edit modal handlers
  const handleEditAddress = () => {
    // Get current contact data from the domain, handling both old and new API structures
    const contacts = domain?.data?.contacts || {};
    const currentContact = domain?.registrant || contacts?.owner || contacts?.registrant || {};
    
    // Initialize editing form with current data
    const initialData = {
      firstName: currentContact.firstName || currentContact.first_name || '',
      lastName: currentContact.lastName || currentContact.last_name || '',
      email: currentContact.email || '',
      phone: currentContact.phone || '',
      address1: currentContact.address1 || currentContact.addressLine1 || '',
      city: currentContact.city || '',
      state: currentContact.state || '',
      postalCode: currentContact.postalCode || currentContact.postal_code || '',
      country: currentContact.country || ''
    };
    
    setEditingAddress(initialData);
    setShowAddressEditModal(true);
  }

  const handleSaveAddress = async () => {
    if (!editingAddress || !domainName) return;
    
    try {
      // Prepare contact data in the format expected by the API
      const contactData = {
        owner: {
          firstName: editingAddress.firstName || editingAddress.first_name || '',
          lastName: editingAddress.lastName || editingAddress.last_name || '',
          email: editingAddress.email || '',
          phone: editingAddress.phone || '',
          address1: editingAddress.address1 || editingAddress.addressLine1 || '',
          city: editingAddress.city || '',
          state: editingAddress.state || '',
          postalCode: editingAddress.postalCode || editingAddress.postal_code || '',
          country: editingAddress.country || ''
        }
      };
      
      await updateContactsMutation.mutateAsync({
        domain: domainName,
        contactData
      });
      
      setShowAddressEditModal(false);
      setEditingAddress(null);
    } catch (error) {
      console.error('Failed to update domain contacts:', error);
      // You might want to show an error toast here
    }
  }

  const handleEditNameservers = () => {
    // Get current nameservers from domain data, handle both string and object formats
    const currentNameservers = domain?.nameservers || domain?.data?.nameservers || [];
    const nameserverStrings = currentNameservers.map(ns => 
      typeof ns === 'string' ? ns : ns?.name || ''
    ).filter(Boolean);
    
    setEditingNameservers([...nameserverStrings]);
    setShowNameserversEditModal(true);
  }

  const handleSaveNameservers = async () => {
    if (!domainName) return;
    
    try {
      // Filter out empty nameservers and prepare data for API
      const validNameservers = editingNameservers
        .filter(ns => ns.trim() !== '')
        .map(ns => ({ name: ns.trim() }));
      
      if (validNameservers.length === 0) {
        alert('At least one nameserver is required');
        return;
      }
      
      await updateNameserversMutation.mutateAsync({
        domain: domainName,
        nameservers: validNameservers
      });
      
      setShowNameserversEditModal(false);
      setEditingNameservers([]);
    } catch (error) {
      console.error('Failed to update nameservers:', error);
      alert('Failed to update nameservers. Please try again.');
    }
  }

  const handleAddNameserver = () => {
    setEditingNameservers([...editingNameservers, ''])
  }

  const handleRemoveNameserver = (index) => {
    setEditingNameservers(editingNameservers.filter((_, i) => i !== index))
  }

  const handleNameserverChange = (index, value) => {
    const updated = [...editingNameservers]
    updated[index] = value
    setEditingNameservers(updated)
  }

  // Auto renewal handlers
  const handleEditAutoRenewal = () => {
    // Initialize the modal with current auto-renewal status from merged domain (includes DB data)
    const currentAutoRenewal = mergedDomain?.data?.auto_renew ?? mergedDomain?.data?.autoRenew ?? mergedDomain?.autoRenew ?? false;
    console.log('🔍 handleEditAutoRenewal - currentAutoRenewal:', currentAutoRenewal);
    setAutoRenewalEnabled(currentAutoRenewal);
    setShowAutoRenewalModal(true);
  }

  const handleSaveAutoRenewal = async () => {
    if (!domainName) return;
    
    try {
      await updateAutoRenewMutation.mutateAsync({
        domain: domainName,
        autoRenew: autoRenewalEnabled // boolean: true or false
      });
      
      setShowAutoRenewalModal(false);
    } catch (error) {
      console.error('Failed to update auto-renewal:', error);
      // Error toast is handled in the mutation
    }
  }

  // Privacy handlers
  const handleEditPrivacy = () => {
    const currentPrivacyState = whoisQueryResp?.data?.whoisPrivacyState || domain?.data?.whoisPrivacyState;
    const isPrivate = currentPrivacyState === 'enable' || currentPrivacyState === true || String(currentPrivacyState).toLowerCase() === 'true';
    setPrivacySetting(isPrivate ? 'private' : 'public');
    setShowPrivacyModal(true);
  }

  const handleSavePrivacy = async () => {
    if (!domainName) return;
    
    try {
      const privacyState = privacySetting === 'private' ? 'enable' : 'disable';
      console.log('Updating WHOIS privacy to:', privacyState);
      
      // Call mutation - error handling and cache updates are handled in the mutation hook
      await updateWhoisPrivacyMutation.mutateAsync({
        domain: domainName,
        state: privacyState
      });

      setShowPrivacyModal(false);
    } catch (error) {
      console.error('Failed to update WHOIS privacy:', error);
      // Error toast is handled in the mutation hook
    }
  }

  // Pagination logic
  const totalPages = Math.ceil(dnsRecords.length / recordsPerPage)
  const startIndex = (currentPage - 1) * recordsPerPage
  const currentRecords = dnsRecords.slice(startIndex, startIndex + recordsPerPage)

  return (
    <div className="max-w-screen mx-auto">
      {isLoadingDomain || isLoadingDNS ? (
        <PageLoader text="Loading domain details..." />
      ) : (
        <>
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={handleBackToMyDomains}
          className="text-[#71717B] cursor-pointer text-sm flex items-center gap-1"
        >
          <img src={chevronLeft}></img> My Domains
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <div className="flex items-start">
          <div className="flex items-center gap-4 px-4">
            <h1 className="text-2xl font-semibold text-gray-900">{domain?.name || domainName}</h1>
            <Badge className="whitespace-nowrap" color={isProjectConnected ? 'lime' : 'zinc'}>
              {isProjectConnected ? 'Connected' : 'Not Connected'}
            </Badge>
          </div>
        </div>

        <div className="px-4">
          <div className="text-sm text-[#71717B] mb-1">Project</div>
          {isProjectConnected ? (
            <div className="text-sm font-medium text-[#0F172A]">{projectName || 'Project'} </div>
          ) : (
            !isViewer && (
              <ConnectProjectDropdown
                projects={projects.filter(project => {
                  const projectId = project.id || project._id;
                  // Filter out projects that are already connected to any domain
                  return !isProjectConnectedToAnyDomain(projectId);
                })}
                onSelect={async (project) => {
                  try {
                    await addDomainWithNsMutation.mutateAsync({
                      domainData: {
                        name: domainName,
                        expiry_date: domain?.data?.registryExpirationDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                      },
                      project,
                      workspaceId: effectiveWorkspaceId,
                    })
                    try { await checkStatusMutation.mutateAsync({ workspaceId: effectiveWorkspaceId, domain: domainName }) } catch {}
                  } catch (e) {
                    console.error('Failed to connect project:', e)
                  }
                }}
                buttonText={<span className="inline-flex items-center justify-start gap-1 font-medium">Connect project <EditIcon16 className="w-[16px] h-[16px]" /></span>}
                buttonClassName="text-sm underline text-black inline-flex items-center justify-start gap-1 -ml-2 p-2 hover:bg-[#F4F4F5] rounded-lg"
              />
            )
          )}
          
        </div>
        <div className="px-4">
          <div className="text-sm text-[#71717B] mb-1">Project plan</div>
          <div className="text-sm font-medium text-[#0F172A]">{domain?.projectPlan || 'Basic'}</div>
        </div>
      </div>

    
      {/* Domain Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        {/* Left Column - Domain Details */}
        <div className="lg:col-span-1 space-y-6">
          {isDomainError ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <div className="text-center">
                <p className="text-red-600">Error loading domain info: {domainError?.message}</p>
                <Button 
                  onClick={() => window.location.reload()} 
                  outline 
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <DomainInfoCard 
              domain={mergedDomain}
              domainName={domainName}
              onEditAddress={isViewer ? null : handleEditAddress}
              onEditNameservers={isViewer ? null : handleEditNameservers}
              onEditAutoRenewal={isViewer ? null : handleEditAutoRenewal}
              onEditPrivacy={isViewer ? null : handleEditPrivacy}
              isViewer={isViewer}
            />
          )}
        </div>

        {/* Right Column - DNS Records */}
        <div className="lg:col-span-2 space-y-6">
          {/* DNS Records Debugger */}
          
          <div className="bg-white ">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:items-center justify-between py-4">
              <Heading level={3} className="text-lg !font-medium whitespace-nowrap">
                Current DNS Record
              </Heading>
              {!isViewer && (
                <Button 
                  onClick={handleAddRecord}
                  disabled={addRecordsMutation.isPending}
                  className="data-focus:!ring-2 whitespace-nowrap data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
                >
                  {addRecordsMutation.isPending ? 'Adding...' : 'ADD New Record'}
                </Button>
              )}
            </div>

            {/* DNS Records Content */}
            {isDNSError ? (
              <div className="p-8 text-center">
                <p className="text-red-600">Error loading DNS records: {dnsError?.message}</p>
                <Button 
                  onClick={() => window.location.reload()} 
                  outline 
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            ) : dnsRecords.length > 0 ? (
              <>
                <DNSRecordsTable
                  key={`dns-table-${tableKey}`}
                  records={currentRecords}
                  onDelete={isViewer ? null : handleDeleteRecord}
                  onEdit={isViewer ? null : handleEditRecord}
                  isViewer={isViewer}
                />

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                      >
                        Back
                      </button>
                  
                  <div className="flex gap-1">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`px-3 py-1 text-sm border rounded data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 ${
                          currentPage === index + 1
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
              </>
            ) : (
              <div className="p-8 text-center">
                <p className="text-gray-600">No DNS records found for this domain.</p>
                {!isViewer && (
                  <Button 
                    plain
                    onClick={handleAddRecord}
                    className="mt-4"
                  >
                    Add Your First Record
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      {/* Add DNS Record Modal */}
      <AddDNSRecordModal
        open={showAddRecordModal}
        onClose={() => setShowAddRecordModal(false)}
        onAdd={handleSaveNewRecord}
      />

      {/* Edit Address Modal */}
      <EditRegistrantContactDialog
        open={showAddressEditModal}
        onClose={() => {
          setShowAddressEditModal(false)
          setEditingAddress(null)
        }}
        domainName={domain?.name || domainName}
        address={editingAddress}
        onAddressChange={setEditingAddress}
        onSave={handleSaveAddress}
        isLoading={updateContactsMutation.isPending}
      />

      {/* Edit Nameservers Modal */}
      <EditNameserversDialog
        open={showNameserversEditModal}
        onClose={() => {
          setShowNameserversEditModal(false)
          setEditingNameservers([])
        }}
        domainName={domain?.name || domainName}
        nameservers={editingNameservers}
        onNameserverChange={handleNameserverChange}
        onAddNameserver={handleAddNameserver}
        onRemoveNameserver={handleRemoveNameserver}
        onSave={handleSaveNameservers}
        isLoading={updateNameserversMutation.isPending}
      />

      {/* Auto Renewal Modal */}
      <AutoRenewalSettingsDialog
        open={showAutoRenewalModal}
        onClose={() => setShowAutoRenewalModal(false)}
        domainName={domain?.name || domainName}
        autoRenewalEnabled={autoRenewalEnabled}
        onAutoRenewalChange={setAutoRenewalEnabled}
        onSave={handleSaveAutoRenewal}
        isLoading={modifySettingsMutation.isPending}
      />

      {/* Privacy Settings Modal */}
      <WhoisPrivacySettingsDialog
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        domainName={domain?.name || domainName}
        privacySetting={privacySetting}
        onPrivacySettingChange={setPrivacySetting}
        renewalPrice={renewalPrice}
        onSave={handleSavePrivacy}
        isLoading={updateWhoisPrivacyMutation.isPending}
      />

      {/* Edit DNS Record Modal */}
      <EditDNSRecordDialog
        open={showEditRecordModal}
        onClose={() => setShowEditRecordModal(false)}
        domainName={domainName}
        record={editingRecord}
        onRecordChange={setEditingRecord}
        onSave={handleSaveEditRecord}
        isLoading={updateRecordMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteDNSRecordDialog
        open={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        record={deletingRecord}
        onConfirm={handleConfirmDelete}
        isLoading={deleteRecordMutation.isPending}
      />
    </div>
  )
}
